import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, DollarSign, Clock, CheckCircle, AlertCircle, Download, ArrowLeft, Trash2, Edit2, X, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkinLoading, setCheckinLoading] = useState({});
  const [editingBooking, setEditingBooking] = useState(null);
  const [editForm, setEditForm] = useState({ tour_date: '', tour_time: '' });
  const [updateLoading, setUpdateLoading] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchBookingsByDate(selectedDate);
    } else {
      fetchBookings();
    }
  }, [selectedDate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      } else {
        setError('Failed to fetch bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Error fetching bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/stats`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching booking stats:', error);
    }
  };

  const fetchBookingsByDate = async (date) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/date/${date}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      } else {
        setError('Failed to fetch bookings for selected date');
      }
    } catch (error) {
      console.error('Error fetching bookings by date:', error);
      setError('Error fetching bookings by date');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (bookingId, currentStatus) => {
    try {
      setCheckinLoading(prev => ({ ...prev, [bookingId]: true }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          checked_in: !currentStatus,
          status: currentStatus ? 'confirmed' : 'checked_in'
        })
      });

      if (response.ok) {
        // Refresh bookings
        if (selectedDate) {
          fetchBookingsByDate(selectedDate);
        } else {
          fetchBookings();
        }
        fetchStats();
      } else {
        setError('Failed to update check-in status');
      }
    } catch (error) {
      console.error('Error updating check-in:', error);
      setError('Error updating check-in');
    } finally {
      setCheckinLoading(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const downloadTicket = async (bookingId, bookingReference) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/ticket`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `UmoonaTourTicket-${bookingReference}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download ticket');
      }
    } catch (error) {
      console.error('Error downloading ticket:', error);
      setError('Error downloading ticket');
    }
  };

  const handleDeleteBooking = async (bookingId, bookingReference) => {
    if (!window.confirm(`Are you sure you want to delete booking ${bookingReference}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        // Refresh bookings list
        if (selectedDate) {
          fetchBookingsByDate(selectedDate);
        } else {
          fetchBookings();
        }
        fetchStats();
        setError(''); // Clear any previous errors
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to delete booking');
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      setError('Error deleting booking');
    }
  };

  const startEditing = (booking) => {
    setEditingBooking(booking.id || booking._id);
    setEditForm({
      tour_date: booking.tour_date,
      tour_time: booking.tour_time
    });
  };

  const cancelEditing = () => {
    setEditingBooking(null);
    setEditForm({ tour_date: '', tour_time: '' });
  };

  const handleUpdateTour = async (bookingId) => {
    try {
      setUpdateLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          tour_date: editForm.tour_date,
          tour_time: editForm.tour_time
        })
      });

      if (response.ok) {
        // Refresh bookings
        if (selectedDate) {
          fetchBookingsByDate(selectedDate);
        } else {
          fetchBookings();
        }
        setEditingBooking(null);
        setEditForm({ tour_date: '', tour_time: '' });
        setError('');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update booking');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      setError('Error updating booking');
    } finally {
      setUpdateLoading(false);
    }
  };

  const getStatusBadge = (booking) => {
    if (booking.checked_in) {
      return <Badge className="bg-green-100 text-green-800">Checked In</Badge>;
    }
    if (booking.status === 'confirmed') {
      return <Badge className="bg-blue-100 text-blue-800">Confirmed</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const timeLabels = {
      '10:00': '10:00 AM',
      '14:00': '2:00 PM',
      '16:00': '4:00 PM'
    };
    return timeLabels[timeString] || timeString;
  };

  const getTicketSummary = (tickets) => {
    return tickets.map(ticket => {
      const typeLabels = {
        adult: 'Adult',
        child: 'Child', 
        family: 'Family'
      };
      return `${ticket.quantity}x ${typeLabels[ticket.ticket_type] || ticket.ticket_type}`;
    }).join(', ');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline" 
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tour Bookings</h1>
            <p className="text-gray-600">Manage underground mine tour bookings and check-ins</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Filter by Date</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
            />
          </div>
          {selectedDate && (
            <Button
              variant="outline"
              onClick={() => setSelectedDate('')}
              className="mt-6"
            >
              Show All
            </Button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_bookings || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Confirmed Bookings</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.confirmed_bookings || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Tours</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.todays_bookings || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(stats.total_revenue || 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedDate ? `Bookings for ${formatDate(selectedDate)}` : 'Recent Bookings'}
          </CardTitle>
          <CardDescription>
            {bookings.length} booking{bookings.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No bookings found for the selected criteria.
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div
                  key={booking.id || booking._id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <div>
                          <div className="font-semibold text-lg">
                            {booking.booking_reference}
                          </div>
                          <div className="text-sm text-gray-600">
                            {booking.customer.name} • {booking.customer.email}
                          </div>
                        </div>
                        {getStatusBadge(booking)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="font-medium text-gray-900">Tour Date</div>
                          {editingBooking === (booking.id || booking._id) ? (
                            <Input
                              type="date"
                              value={editForm.tour_date}
                              onChange={(e) => setEditForm({ ...editForm, tour_date: e.target.value })}
                              className="w-full mt-1"
                            />
                          ) : (
                            <div className="text-gray-600">{formatDate(booking.tour_date)}</div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Tour Time</div>
                          {editingBooking === (booking.id || booking._id) ? (
                            <Select
                              value={editForm.tour_time}
                              onValueChange={(value) => setEditForm({ ...editForm, tour_time: value })}
                            >
                              <SelectTrigger className="w-full mt-1">
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="10:00">10:00 AM</SelectItem>
                                <SelectItem value="14:00">2:00 PM</SelectItem>
                                <SelectItem value="16:00">4:00 PM</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="text-gray-600">{formatTime(booking.tour_time)}</div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Tickets</div>
                          <div className="text-gray-600">{getTicketSummary(booking.tickets)}</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Total</div>
                          <div className="text-gray-600 font-semibold">
                            ${booking.total_amount.toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {/* Edit controls */}
                      {editingBooking === (booking.id || booking._id) && (
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateTour(booking.id || booking._id)}
                            disabled={updateLoading}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {updateLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-1" />
                                Save Changes
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                            disabled={updateLoading}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      )}

                      {booking.customer_notes && (
                        <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                          <div className="font-medium text-blue-900">Customer Notes:</div>
                          <div className="text-blue-800">{booking.customer_notes}</div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        size="sm"
                        variant={booking.checked_in ? "default" : "outline"}
                        onClick={() => handleCheckIn(booking.id || booking._id, booking.checked_in)}
                        disabled={checkinLoading[booking.id || booking._id]}
                        className={booking.checked_in ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {checkinLoading[booking.id || booking._id] ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : booking.checked_in ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Checked In
                          </>
                        ) : (
                          <>
                            <Users className="w-4 h-4 mr-1" />
                            Check In
                          </>
                        )}
                      </Button>

                      {editingBooking !== (booking.id || booking._id) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditing(booking)}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit Tour
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadTicket(booking.id || booking._id, booking.booking_reference)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Ticket
                      </Button>

                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteBooking(booking.id || booking._id, booking.booking_reference)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBookingsPage;